import React, { useState } from 'react';
import './GeoReportPreview.scss';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import { format } from 'date-fns';
import { GeoReportResponse } from '../../../../shared/models/geo-report/GeoReportResponse';
import { ChatbeatPdfService } from '../../../../shared/services/pdf/ChatbeatPdfService';
import GeoReportPdfDocument from '../geo-report-pdf/GeoReportPdf';

interface GeoReportPreviewProps {
  report: GeoReportResponse;
}

const getScoreColor = (score: number): string => {
  if (score >= 70) return 'high';
  if (score >= 40) return 'mid';
  return 'low';
};

const getScoreLabel = (score: number): string => {
  if (score >= 80) return 'Excellent';
  if (score >= 70) return 'Good';
  if (score >= 50) return 'Needs Improvement';
  return 'Critical';
};

/**
 * Minimal markdown-to-HTML converter with card-based layout.
 *
 * Sections (h2 headings) are wrapped in card containers. The first section
 * (h1 + Executive Summary) is treated as a cover/hero area.
 *
 * Table detection: a block is treated as a markdown table when it contains at least two
 * consecutive lines that start and end with `|`, with a separator row (`|---|`) between
 * the header row and the body rows.
 */
const markdownToHtml = (md: string): string => {
  // HTML-escape the raw source first to prevent XSS
  const escaped = md.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // Normalize line endings
  const normalized = escaped.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Extract markdown tables using multiline regex before line-by-line processing.
  const tableRegex = /(?:^[ \t]*\|[^\n]*\|[ \t]*\n){3,}/gm;
  const tables: string[] = [];
  const PLACEHOLDER = '\x00TABLE_';

  const withPlaceholders = normalized.replace(tableRegex, (match) => {
    const lines = match
      .trim()
      .split('\n')
      .map((l: string) => l.trim());

    const sepIdx = lines.findIndex((l: string) => /^\|[-\s|:]+\|$/.test(l));
    if (sepIdx < 0) return match;

    let html = '<table>';
    let inBody = false;
    for (const tl of lines) {
      if (/^\|[-\s|:]+\|$/.test(tl)) {
        inBody = true;
        continue;
      }
      const cells = tl
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((c: string) => c.trim());
      const tag = inBody ? 'td' : 'th';
      html += `<tr>${cells.map((c) => `<${tag}>${styleCellContent(c)}</${tag}>`).join('')}</tr>`;
    }
    html += '</table>';

    const idx = tables.length;
    tables.push(html);
    return `${PLACEHOLDER}${idx}\n`;
  });

  // Process remaining lines
  let result = withPlaceholders
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '\x01SECTION_BREAK\x01<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, (m) => `<ul>${m}</ul>`)
    .replace(/\n\n/g, '</p><p>')
    .replace(/^(?!<[hult\x00\x01])(.+)$/gm, '<p>$1</p>')
    .replace(/<p><\/p>/g, '');

  // Inject tables back at placeholder positions
  result = result.replace(/\x00TABLE_(\d+)/g, (_match, idx) => tables[parseInt(idx, 10)] || '');

  // Style status indicators (emojis) in content
  result = styleStatusIndicators(result);

  // Wrap sections into cards
  result = wrapSectionsInCards(result);

  return result;
};

/**
 * Style cell content: detect status emojis and score patterns in table cells.
 */
const styleCellContent = (content: string): string => {
  // Strip markdown bold/italic first so badge detection works on clean text
  let styled = content
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1');

  // Style ✅/❌/⚠️ status indicators
  styled = styled
    .replace(/&amp;#x2705;|✅/g, '<span class="report-status report-status--success">✅</span>')
    .replace(/&amp;#x274C;|❌/g, '<span class="report-status report-status--error">❌</span>')
    .replace(/&amp;#x26A0;|⚠️/g, '<span class="report-status report-status--warning">⚠️</span>');

  // Style "Ready", "Blocked", "Partial", "Strong", "Needs Fix" text
  styled = styled
    .replace(/\bReady\b/g, '<span class="report-badge report-badge--success">Ready</span>')
    .replace(/\bBlocked\b/g, '<span class="report-badge report-badge--error">Blocked</span>')
    .replace(/\bPartial\b/g, '<span class="report-badge report-badge--warning">Partial</span>')
    .replace(/\bAllowed \(default\)/g, '<span class="report-badge report-badge--success">Allowed</span>')
    .replace(/\bNot Mentioned\b/g, '<span class="report-badge report-badge--success">Allowed</span>')
    .replace(/\bAllowed\b/g, '<span class="report-badge report-badge--success">Allowed</span>')
    .replace(/\bStrong\b/g, '<span class="report-badge report-badge--success">Strong</span>')
    .replace(/\bNeeds Fix\b/g, '<span class="report-badge report-badge--warning">Needs Fix</span>');

  // Style severity labels
  styled = styled
    .replace(/\bCritical\b/g, '<span class="report-severity report-severity--critical">Critical</span>')
    .replace(/\bHigh\b/g, '<span class="report-severity report-severity--high">High</span>')
    .replace(/\bMedium\b/g, '<span class="report-severity report-severity--medium">Medium</span>')
    .replace(/\bLow\b/g, '<span class="report-severity report-severity--low">Low</span>');

  return styled;
};

/**
 * Style emoji status indicators in body content.
 */
const styleStatusIndicators = (html: string): string => {
  return html
    .replace(/✅/g, '<span class="report-status report-status--success">✅</span>')
    .replace(/❌/g, '<span class="report-status report-status--error">❌</span>')
    .replace(/⚠️/g, '<span class="report-status report-status--warning">⚠️</span>');
};

/**
 * Wrap h2 sections into card containers.
 * Content before the first h2 (title + executive summary) gets a hero wrapper.
 */
const wrapSectionsInCards = (html: string): string => {
  const parts = html.split('\x01SECTION_BREAK\x01');

  if (parts.length <= 1) return html;

  // Skip the first part entirely — the cover section already shows title + score.
  // Remaining parts = cards (each starts with <h2>)
  const cards = parts.slice(1).map((section) => {
    return `<div class="report-card">${section}</div>`;
  });

  return cards.join('');
};

const GeoReportPreview: React.FC<GeoReportPreviewProps> = ({ report }) => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);

  const handleDownloadPdf = async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      const filename = `GEO-Report-${report.url
        .replace(/https?:\/\//g, '')
        .replace(/[^a-z0-9]/gi, '-')}-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      const doc = <GeoReportPdfDocument report={report} />;
      await ChatbeatPdfService.generate(doc, filename);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const htmlContent = report.reportMarkdown ? markdownToHtml(report.reportMarkdown) : '';
  const score = report.geoScore ?? 0;
  const scoreColor = getScoreColor(score);
  const scoreLabel = getScoreLabel(score);

  return (
    <div className="geo-report-preview">
      <div className="geo-report-preview__actions">
        <Button id="geo-report-preview-download" className="action-btn" onClick={handleDownloadPdf} disabled={isExporting}>
          {isExporting ? (
            <span className="ico--loader ico--xs" />
          ) : (
            <span className="ico--download ico--xs" />
          )}
        </Button>
        {!isExporting && (
          <CustomTooltip tooltipText="geoReport.downloadPdf" targetRef="geo-report-preview-download" />
        )}
      </div>

      {htmlContent ? (
        <div className="geo-report-preview__report">
          <div className={`geo-report-preview__cover geo-report-preview__cover--${scoreColor}`}>
            <div className="geo-report-preview__cover-label">GEO Audit</div>
            <div className="geo-report-preview__cover-url">{report.url.replace(/^https?:\/\//, '')}</div>
            <div className={`geo-report-preview__cover-score geo-report-preview__cover-score--${scoreColor}`}>
              {score}
            </div>
            <div className="geo-report-preview__cover-max">/100</div>
            <div className={`geo-report-preview__cover-status geo-report-preview__cover-status--${scoreColor}`}>
              {scoreLabel}
            </div>
          </div>
          <div className="geo-report-preview__content" dangerouslySetInnerHTML={{ __html: htmlContent }} />
        </div>
      ) : (
        <p className="geo-report-preview__empty">{t('geoReport.noContent')}</p>
      )}
    </div>
  );
};

export { GeoReportPreview };
export default GeoReportPreview;
